<template>
  <div class="home">
    <div class="home__search">
      <h1 class="f-title-32-bold t-fg-primary" v-text="$t('general.howCanWeHelpYou')" />
      <p-advanced-search :is-home="true" :placeholder="$t('general.search')" />
    </div>
    <div class="home__quick-solutions">
      <p-page-subtitle :title="$t('general.quickSolutions')" />
      <div class="home__quick-solutions-grid">
        <router-link
          v-for="item in quickSolutions"
          :key="item.slug.en"
          :to="appLink(item.slug[langKey])"
          class="home__quick-solution"
        >
          <p-icon
            :icon="item.icon"
            :size="$device.isMobile ? 'ms' : 'md'"
            color="fg-accent"
            class="p-12"
          />
          <div
            class="home__quick-solution-title f-utility-14-medium t-fg-secondary text-center"
            v-html="formatQuickSolutionTitle(item.title)"
          ></div>
        </router-link>
      </div>
    </div>

    <div class="home__popular-articles">
      <p-page-subtitle :title="$t('general.popularArticles')" />
      <div class="home__popular-articles-grid">
        <p-article-link
          v-for="article in popularArticles"
          :key="article.slug.en"
          size="lg"
          :label="$t(article.title)"
          :to="appLink(article.slug[langKey])"
          class="home__popular-articles-item"
        />
      </div>
    </div>

    <div v-if="!loading" class="home__latest-announcements">
      <div class="home__latest-announcements-header">
        <p-page-subtitle :title="$t('general.latestAnnouncements')" />
        <p-link
          size="md"
          :label="$t('general.allAnnouncements')"
          :to="appLink('announcement')"
          class="home__latest-announcements-view-all"
          underline-color="primary"
          append-icon="caret-right"
          icon-size="xmd"
        />
      </div>
      <div class="home__latest-announcements-list">
        <template v-if="$device.isMobile">
          <p-home-mobile-announcement
            v-for="(announcement, index) in latestAnnouncements?.items"
            :key="`announcement-${index}`"
            :icon="announcement.icon_url"
            :title="announcement.category_title"
            :text="announcement.title"
            :link="appLink(['announcement', announcement.slug])"
          />
        </template>
        <template v-else>
          <p-announcement
            v-for="(announcement, index) in latestAnnouncements?.items"
            :key="`announcement-${index}`"
            :icon="announcement.icon_url"
            :category="announcement.category_title"
            :category-link="appLink(['announcement', announcement.category_slug])"
            :date="announcement.published_at"
            :reading-time="announcement.read_duration_minutes"
            :heading="announcement.title"
            :content="getShortAnnouncementContent(announcement.summary)"
            :link="appLink(['announcement', `${announcement.category_slug}/${announcement.slug}`])"
            :divider="false"
          />
        </template>
      </div>
    </div>

    <div v-if="rootCategories && 0 < rootCategories.length" class="home__support-topics">
      <p-page-subtitle :title="$t('general.supportTopics')" />
      <p-side-navigation
        :items="
          rootCategories.map((category) => ({
            ...category,
            link: appLink(category.slug),
          }))
        "
        :mode="$device.isMobile ? 'vertical' : 'horizontal'"
        @item-click="handleSupportTopicClick"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import PAnnouncement from '~/components/pages/Announcement/PAnnouncement/PAnnouncement.vue'
import PAdvancedSearch from '~/components/pages/Search/PAdvancedSearch/PAdvancedSearch.vue'
import PArticleLink from '~/components/ui/PArticleLink/PArticleLink.vue'

import { useLatestAnnouncements } from '@/api/useAnnouncement'
import { useRootCategories } from '@/api/useCategories'
import { useAppLink } from '@/composables/useAppLink'

// removed unused product assets

const { appLink } = useAppLink()

const { latestAnnouncements, loading } = useLatestAnnouncements()
const { rootCategories } = useRootCategories()

const language = computed(() => useI18n().locale.value.split('-')[0])
const langKey = computed<'tr' | 'en'>(() => (language.value === 'en' ? 'en' : 'tr'))

const quickSolutions = computed(() => [
  {
    icon: 'key',
    title: 'quickSolutions.forgotPassword',
    slug: {
      tr: 'guvenlik/parolami-unuttum-ne-yapmaliyim',
      en: 'forgot-password',
    },
  },
  {
    icon: 'try',
    title: 'quickSolutions.tlDepositAndWithdrawal',
    slug: {
      tr: 'cuzdan/paribu-hesabima-nasil-tl-yatiririm',
      en: 'tl-deposit-and-withdrawal',
    },
  },
  {
    icon: 'identity',
    title: 'quickSolutions.identityVerification',
    slug: {
      tr: 'baslerken/paribuda-hesap-dogrulama-nasil-yapilir',
      en: 'identity-verification',
    },
  },
  {
    icon: 'email-message-inbox',
    title: 'quickSolutions.changeEmailAddress',
    slug: {
      tr: 'guvenlik/kullanici-bilgilerimi-nasil-guncellerim',
      en: 'change-email-address',
    },
  },
  {
    icon: 'phone',
    title: 'quickSolutions.changePhoneNumber',
    slug: {
      tr: 'guvenlik/telefon-numarasini-degistirmek-istiyorum-ne-yapmaliyim',
      en: 'change-phone-number',
    },
  },
  {
    icon: 'stop',
    title: 'quickSolutions.deactivateAccount',
    slug: {
      tr: 'paribu-hakkinda/paribu-resmi-iletisim-kanallari-nelerdir',
      en: 'deactivate-account',
    },
  },
])
const popularArticles = computed(() => [
  {
    title: 'popularArticles.identityVerification',
    slug: {
      tr: 'kimlik-dogrulama',
      en: 'identity-verification',
    },
  },
  {
    title: 'popularArticles.lostDeviceAndReport',
    slug: {
      tr: 'kayip-cihaz-ve-rapor',
      en: 'lost-device-and-report',
    },
  },
  {
    title: 'popularArticles.emailChange',
    slug: {
      tr: 'eposta-degisikligi',
      en: 'email-change',
    },
  },
  {
    title: 'popularArticles.withdrawTL',
    slug: {
      tr: 'tl-cekme',
      en: 'withdraw-tl',
    },
  },
  {
    title: 'popularArticles.commission',
    slug: {
      tr: 'komisyon',
      en: 'commission',
    },
  },
])
// removed unused paribuProducts computed

const handleSupportTopicClick = (item: { icon: string; title: string; link?: string }) => {
  if (item.link) {
    window.location.href = item.link
  }
}

const getShortAnnouncementContent = (content: string) => {
  return content?.length > 130 ? content?.slice(0, 130) + '...' : content
}

const formatQuickSolutionTitle = (i18nKey: string) => {
  const text = String($t(i18nKey))
  // Prefer explicit breaks for known long labels to ensure two lines
  const manualMap: Record<string, string> = {
    'quickSolutions.forgotPassword': 'Parolamı<br/>Unuttum',
    'quickSolutions.identityVerification': 'Kimlik<br/>Doğrulama',
    'quickSolutions.deactivateAccount': 'Hesap<br/>Dondurma',
  }
  if (manualMap[i18nKey]) return manualMap[i18nKey]

  const words = text.split(' ')
  if (words.length <= 2) return text
  const firstPart = words.slice(0, words.length - 1).join(' ')
  const lastWord = words[words.length - 1]
  return `${firstPart}<br/>${lastWord}`
}

definePageMeta({
  layout: 'default',
})
</script>
